
/**
 * @file dcp/src/dcp-client/worker/supervisor2/common.js
 *
 * A support class for Supervisor2.
 *
 * @author Paul, paul@distributive.network
 * @date Feb-April 2023,
 * @module common
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

const unscopedDebugging = require('dcp/debugging');
const debugging = unscopedDebugging.scope('worker');
const _selectiveSup = unscopedDebugging.scope('sup');
const _selectiveSupEx = unscopedDebugging.scope('supex');
const { assert } = require('dcp/common/dcp-assert');
const DCP_ENV = require('dcp/common/dcp-env');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const { Inventory } = require('dcp/utils/inventory');
const utils = require('dcp/utils');
const wd = require('dcp/utils/worker-and-daemons');

/** @typedef {import('./slice2').Slice} Slice                          */
/** @typedef {import('./index').Supervisor} Supervisor                 */
/** @typedef {import('dcp/utils/jsdoc-types').RetSliceObj} RetSliceObj */

/**
 * Adjust delay times when debugging.
 * The adjustment for niim is automatic, other debuggers must manually change this value.
 */
let timeDilation = 1;
if (DCP_ENV.platform === 'nodejs')
{
  /** Make timers 20x slower when running in niim */
  if (!DCP_ENV.getenv('DCP_SUPERVISOR_TIMEOUTS_DEBUG'))
    timeDilation = (requireNative('module')._cache.niim instanceof requireNative('module').Module) ? 20 : 1;
}

//
// Global constants
//
const schedulingSlop = 0.25;
const debugBuild = (require('dcp/common/dcp-build').build === 'debug');
const addressTruncationLength = 20;

//
// Flags for tracing.
// NOTE: I definitely want to be able to dynamically turn on debug tracing for a running worker that's
//       acting weird. The only thing I can think of is to use an unused SIGNAL (e.g. between 31 and 64)
//       to send the SIGNAL to the worker process.  Try this soon.
//
function selectiveSupEx ()
{
  return false || _selectiveSupEx()
    || selectiveDebug();
}
function selectiveDebug ()
{
  return _selectiveSup()
    || debugging();
}
function selectiveDebug2 ()
{
  return debugging('supervisor');
}
function selectiveCatch (level = 0) // To display traces in certain catch handlers
{
  // NOT READY YET
  // const flags = [ selectiveSupEx, selectiveDebug, selectiveDebug2 ];
  // return flags?.[level]()
  //   || Boolean(DCP_ENV.getenv('DCP_SUPERVISOR_SHOW_ERRORS'));
  return false;
}

/**
 * Construct a reason for returning a slice to scheduler.
 * @param {{ message: string|false }} error - error-like
 * @param {string}  [str]
 * @returns {string|{errorCode: string, message: string|false}}}
 */
function errorCodeOrString (error, str)
{
  // Prefer errorCode because status.js limits the # of times of slice can be returned for a given errorCode.
  // E.g. 'EUNCAUGHT', 'ENOMODULE', 'ENOPROGRESS', 'ESLICETOOSLOW'
  return error['errorCode'] ? error['errorCode'] : ((error['code'] ?? str) ?? error.message);
}
exports.errorCodeOrString = errorCodeOrString;

/**
   * Accumulate value into acc.
   * @param {{ total: number, CPU: number, webGL: number, webGPU: number }} acc
   * @param {{ total: number, CPU: number, webGL: number, webGPU: number }} value
   */
function accumulateTime (acc, value)
{
  acc['total']  += value['total'];
  acc['CPU']    += value['CPU'];
  acc['webGL']  += value['webGL'];
  acc['webGPU'] += value['webGPU'];
}

/**
 * Remove element from array.
 * @param {Array<*>} array
 * @param {*}        element
 * @return {boolean} - False when element is not in array.
 */
function removeElement (array, element)
{
  let index = array.indexOf(element);
  if (index === -1)
    return false;
  array.splice(index, 1);
  return true;
}

/**
 * JSDoc type descriptor checking needs to interpret Inventory as an Array.
 * @param {string} tag
 * @returns {Array}
 */
function InventoryArray (tag)
{
  // @ts-ignore
  return new Inventory(tag);
}

/**
 * Add a slice to the returnSlice bucket being built. If a sliceBucket already exists for the
 * job-isEstimation-authMessage-reason tuple, then the slice will be added to that, otherwise a new
 * sliceBucket will be added to the buckets.
 * 
 * @param {Slice[]} slices - The slices.
 * @param {string|{message: string|false, errorCode: string}} reason - reason to further characterize status; e.g. 'ENOPROGRESS', 'EUNCAUGHT', 'ESLICETOOSLOW', 'unknown'.
 * @param {RetSliceObj[]} [sliceBuckets] - Optional slice buckets being built. Will be mutated in place.
 * @returns {RetSliceObj[]} - mutated sliceBuckets array
 */
function constructReturnSliceBuckets (slices, reason, sliceBuckets)
{
  assert(slices.length > 0);
  /**
   * @todo XXXpfr When possible, get rid of sliceNumber in status.js because we don't use it.
   * E.g. const { job: jobId, sliceNumber, sliceNumbers, status, isEstimationSlice, reason, authorizationMessage } = sliceGroup;
   */

  // Fast path for 1 element.
  if (slices.length === 1)
  {
    const slice = slices[0];
    slice.killResult();
    return [ slice.getMessage('return', { reason, isEstimationSlice: slice.isEstimation }) ];
  }

  assert(slices.length > 1);
  /** @type {Object<string, RetSliceObj[]>} */
  const jobMap = {};
  for (let k = 0; k < slices.length; k++)
  {
    const slice = slices[k];
    if (slice.killResult)
      slice.killResult();

    if (!jobMap[slice.jobId])
      jobMap[slice.jobId] = [];

    // Try to find a sliceBucket in the buckets which matches the job, estimation status, reason, and auth message.
    const sliceBucket = jobMap[slice.jobId].find(desc => {
      return desc.isEstimationSlice === slice.isEstimation
        && desc.authorizationMessage === slice.authorizationMessage
        && desc.reason === reason;
    });

    if (!sliceBucket)
      jobMap[slice.jobId].push(slice.getMessage('return', { reason, isEstimationSlice: slice.isEstimation }));
    else
      sliceBucket.sliceNumbers.push(slice.sliceNumber);
  }

  if (!sliceBuckets)
    sliceBuckets = [];
  for (const value of Object.values(jobMap))
    sliceBuckets.push(...value);
  return sliceBuckets;
}

/**
 * Organize slices into return buckets based on job, authorization, and status.
 * If there is only one slice, its slice-return payload will be found.
 * @param {string}       workerId
 * @param {Slice[]}      slices
 * @param {string|{message: string|false, errorCode: string}} reason
 * @returns {Object} The compressed slice payload
 */
function compressPayload (workerId, slices, reason)
{
  assert(slices.length > 0);
  return {
    worker: workerId,
    slices: constructReturnSliceBuckets(slices, reason),
  };
}

/**
 * If obj: jobId -> slice then compressJobMap(obj, (slice) => slice.sliceNumber).
 * @param {*} obj - Object whose properties are jobId strings and values are array of slice-like objects.
 * @param {Function} [functor]
 * @returns {string}
 */
function compressJobMap (obj, functor)
{
  return utils.compressJobMap(obj, addressTruncationLength, functor);
}

/**
 * Check whether access with which is valid connection name for supervisor.
 * @param {string} which - 'taskDistributor', 'resultSubmitter', 'eventRouter', or 'packageManager'
 */
function checkConnectionName (which)
{
  const candidates = [ 'taskDistributor', 'resultSubmitter', 'eventRouter', 'packageManager' ];
  if (!candidates.includes(which))
    throw new Error(`${which} is not a vaild name of a supervisor connection`);
}

/**
 * @param {Slice[]} sliceInventory
 * @returns {{ ready: number, reserved: number, working: number, workdone: number, complete: number, failed: number, finished: number, unassigned: number }}
 */
function sliceCounts (sliceInventory)
{
  let ready = 0, reserved = 0, working = 0, workdone = 0, complete = 0, failed = 0, finished = 0, unassigned = 0;
  for (const slice of sliceInventory)
    if (slice.isReady) ready++;
    else if (slice.isReserved) reserved++;
    else if (slice.isWorking)  working++;
    else if (slice.isWorkDone) workdone++;
    else if (slice.isComplete) complete++;
    else if (slice.isFailed)   failed++;
    else if (slice.isFinished) finished++;
    else if (slice.isUnassigned) unassigned++;
    else throw new Error(`sliceCounts: Unexpected kind a slice; ${slice.identifier}`);
  return { ready, reserved, working, workdone, complete, failed, finished, unassigned };
}

/**
 * Flag to display detailed debug info in diagnostics.
 * @return {boolean}
 */
function displayMaxDiagInfo ()
{
  return (Boolean(DCP_ENV.getenv('DCP_SUPERVISOR_DEBUG_DISPLAY_MAX_INFO') || debugging() || false));
}

/**
 * When true, display high priority trace info.
 * @param {boolean} [displayOnDebugBuild]
 * @return {boolean}
 */
function highPriorityDiag (displayOnDebugBuild = false)
{
  return (displayMaxDiagInfo() || selectiveSupEx() || displayOnDebugBuild && debugBuild);
}

//
// Tools for debugging supervisor2.
//
class DebuggingTools
{
  /**
   * @constructor
   * @param {Supervisor} supervisor
   */
  constructor (supervisor)
  {
    /** @type {Supervisor} */
    this.supervisor = supervisor;
  }

  /**
   * Helper to dump slice state of Supervisor.
   * @param {*} tag
   */
  dump (tag)
  {
    console.log('> worker', this.supervisor.workerId, tag);
    for (const jm of this.supervisor.jobManagerInventory)
    {
      console.log('>>  jm', jm.identifier);
      console.log('>>>   slices', jm.sliceInventory.map((s) => s.identifier))
    }
  }

  /**
   * Count the # of assigned, working, terminated, read_for_assign sandboxes.
   * @returns {{ assigned: number, working: number, terminated: number, read_for_assign: number }}
   */
  dumpSandboxState ()
  {
    const assigned = [], working = [], terminated = [], read_for_assign = [];
    for (const sandbox of this.supervisor.sandboxInventory)
    {
      if (sandbox.isAssigned) assigned.push(sandbox);
      else if (sandbox.isWorking) working.push(sandbox);
      else if (sandbox.isTerminated) terminated.push(sandbox);
      else if (sandbox.isReady) read_for_assign.push(sandbox);
    }
    return { assigned: assigned.length, working: working.length, terminated: terminated.length, read_for_assign: read_for_assign.length };
  }

  /** @type {{ ready: number, reserved: number, working: number, workdone: number, complete: number, failed: number, finished: number }} */
  get sliceCounts ()
  {
    let ready = 0, reserved = 0, working = 0, workdone = 0, complete = 0, failed = 0, finished = 0;
    for (const jobMan of this.supervisor.jobManagerInventory)
      for (const slice of jobMan.sliceInventory)
        if (slice.isReady) ready++;
        else if (slice.isReserved) reserved++;
        else if (slice.isWorking)  working++;
        else if (slice.isWorkDone) workdone++;
        else if (slice.isComplete) complete++;
        else if (slice.isFailed)   failed++;
        else if (slice.isFinished) finished++;
    return { ready, reserved, working, workdone, complete, failed, finished };
  }
  /** @type {string} */
  get sliceSandboxStr ()
  {
    return `${this.sliceStr('r/rsv/w/wd/c/fail/fin/wsbx/sbx')}/${this.supervisor.workingSandboxCount}/${this.supervisor.sandboxInventory.length}`;
  }
  /** @type {string} */
  get workingSliceSandboxStr ()
  {
    return `${this.workingSliceStr('r/rsv/w/wsbx/sbx')}/${this.supervisor.workingSandboxCount}/${this.supervisor.sandboxInventory.length}`;
  }
  /**
   * @param {string} [tag='']
   * @returns {string}
   */
  workingSliceStr (tag='')
  {
    if (tag.length < 1) tag = 'r/rsv/w';
    const { ready, reserved, working } = this.sliceCounts;
    return `${tag} ${ready}/${reserved}/${working}`;
  }
  /**
   * @param {string} [tag='']
   * @returns {string}
   */
  sliceStr (tag='')
  {
    if (tag.length < 1) tag = 'r/rsv/w/wd/c/fail/fin';
    const { ready, reserved, working, workdone, complete, failed, finished } = this.sliceCounts;
    return `${tag} ${ready}/${reserved}/${working}/${workdone}/${complete}/${failed}/${finished}`;
  }
}
exports.DebuggingTools = DebuggingTools;

exports.accumulateTime = accumulateTime;
exports.removeElement = removeElement;
exports.InventoryArray = InventoryArray;
exports.compressPayload = compressPayload;
exports.constructReturnSliceBuckets = constructReturnSliceBuckets;
exports.compressJobMap = compressJobMap;
exports.checkConnectionName = checkConnectionName;
exports.sliceCounts = sliceCounts;
exports.displayMaxDiagInfo = displayMaxDiagInfo;

exports.timeDilation = timeDilation;
exports.schedulingSlop = schedulingSlop;
exports.debugBuild = debugBuild;
exports.addressTruncationLength = addressTruncationLength;

exports.selectiveDebug = selectiveDebug;
exports.selectiveDebug2 = selectiveDebug2;
exports.selectiveSupEx = selectiveSupEx;
exports.selectiveCatch = selectiveCatch;
exports.highPriorityDiag = highPriorityDiag;
